// @ts-nocheck
// Reconciles the ProcessedModelSchedule rows for a ProcessedModelSchema (1:many). These rows are what processedModelPollerFunction queries (status=ACTIVE, nextRunAt<=now) to fan out runs. Each schedule has its own id; they are linked to the schema by processedModelSchemaId and listed via the byProcessedModelSchema GSI.
import { generateClient } from "aws-amplify/api";
import {
  createProcessedModelSchedule,
  updateProcessedModelSchedule,
  deleteProcessedModelSchedule as deleteProcessedModelScheduleMutation,
} from "../graphql/mutations";
import { processedModelSchedulesByProcessedModelSchemaIdAndCreatedAt } from "../graphql/queries";
import { computeNextRunFromCron } from "./scheduleFrequency";
import {
  scheduleConfigToRunSchedule,
  summarizeScheduleConfig,
} from "../components/Schedule/scheduleConfig";

const client = generateClient();

const WEEK_DAY = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

function uuid() {
  return crypto.randomUUID();
}

function parseRunSchedule(runSchedule) {
  if (!runSchedule) return null;
  if (typeof runSchedule === "string") {
    try {
      return JSON.parse(runSchedule);
    } catch {
      return null;
    }
  }
  return runSchedule;
}

function toIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Derive ProcessedModelSchedule timing fields from a runSchedule ({ firstRunTime, frequency, cronExpression, timezone }). Prefers an explicit cronExpression (the editor already builds it); falls back to deriving one from a legacy frequency label. `frequency` is preserved as-is so edit mode can re-hydrate the exact chip (recurring stores the chip name, e.g. "Weekdays"). */
export function deriveScheduleTiming(runSchedule) {
  const rs = parseRunSchedule(runSchedule) || {};
  const freqRaw = rs.frequency || "ONCE";
  const up = String(freqRaw).toUpperCase();
  const timezone = rs.timezone || "UTC";
  const firstRunTime = toIso(rs.firstRunTime);

  if (up === "ONCE") {
    return {
      cronExpression: null,
      frequency: freqRaw,
      timezone,
      firstRunTime,
      nextRunAt: firstRunTime || new Date().toISOString(),
    };
  }

  let cronExpression = rs.cronExpression || "";
  if (!cronExpression && up !== "CUSTOM") {
    // Fallback for legacy runSchedule objects that carried only a frequency label, no cron.
    const base = rs.firstRunTime ? new Date(rs.firstRunTime) : new Date();
    const ref = Number.isNaN(base.getTime()) ? new Date() : base;
    const m = ref.getMinutes();
    const h = ref.getHours();
    const dom = ref.getDate();
    const dow = WEEK_DAY[ref.getDay()];
    switch (up) {
      case "HOURLY":
        cronExpression = `${m} * * * ? *`;
        break;
      case "WEEKLY":
        cronExpression = `${m} ${h} ? * ${dow} *`;
        break;
      case "MONTHLY":
        cronExpression = `${m} ${h} ${dom} * ? *`;
        break;
      default:
        cronExpression = `${m} ${h} * * ? *`;
    }
  }

  const nextRunAt = cronExpression
    ? computeNextRunFromCron(cronExpression, timezone)
    : firstRunTime || new Date().toISOString();

  return { cronExpression: cronExpression || null, frequency: freqRaw, timezone, firstRunTime, nextRunAt };
}

export async function fetchSchedulesForSchema(schemaId) {
  const rows = [];
  let nextToken = null;
  do {
    const { data } = await client.graphql({
      query: processedModelSchedulesByProcessedModelSchemaIdAndCreatedAt,
      variables: { processedModelSchemaId: schemaId, nextToken },
    });
    const page = data?.processedModelSchedulesByProcessedModelSchemaIdAndCreatedAt;
    rows.push(...(page?.items || []));
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return rows;
}

/** Reconcile a schema's schedule rows against the editor's list of ScheduleConfigs. - config with an id matching an existing row -> UPDATE in place - config without an id -> CREATE (fresh uuid) - existing row not represented in the list -> DELETE When isActive is false, rows are written/kept INACTIVE (not deleted — preserves runCount/history). Best-effort: each mutation is isolated so one failure doesn't abort the rest. */
export async function reconcileProcessedModelSchedules(
  schemaId,
  scheduleConfigs,
  { isActive, owner, organizationId } = {},
  log
) {
  if (!schemaId) return;
  const status = isActive ? "ACTIVE" : "INACTIVE";
  const configs = Array.isArray(scheduleConfigs) ? scheduleConfigs : [];

  // Optional Operations-Log hooks (the wizard passes its addOperation/updateOperation).
  let seq = 0;
  const startOp = (label) => {
    if (!log?.addOp) return null;
    const opId = `schedule-${schemaId}-${seq++}`;
    log.addOp(opId, "schedule", label, "in_progress");
    return opId;
  };
  const endOp = (opId, error) => {
    if (opId && log?.updateOp) log.updateOp(opId, error ? { status: "error", error } : { status: "success" });
  };

  let existing = [];
  try {
    existing = await fetchSchedulesForSchema(schemaId);
  } catch (e) {
    console.log("reconcileProcessedModelSchedules: failed to list existing rows", e?.message);
  }
  const existingById = new Map(existing.map((r) => [r.id, r]));
  const keptIds = new Set();

  for (const cfg of configs) {
    const timing = deriveScheduleTiming(scheduleConfigToRunSchedule(cfg));
    const fields = {
      status,
      nextRunAt: timing.nextRunAt,
      cronExpression: timing.cronExpression,
      timezone: timing.timezone,
      frequency: timing.frequency,
      firstRunTime: timing.firstRunTime,
    };
    const isUpdate = cfg.id && existingById.has(cfg.id);
    const opId = startOp(`Schedule: ${summarizeScheduleConfig(cfg)}${isUpdate ? " (update)" : ""}`);
    try {
      if (isUpdate) {
        keptIds.add(cfg.id);
        await client.graphql({
          query: updateProcessedModelSchedule,
          variables: { input: { id: cfg.id, ...fields } },
        });
      } else {
        const input = { id: uuid(), processedModelSchemaId: schemaId, ...fields };
        if (owner) input.owner = owner;
        if (organizationId) input.organizationId = organizationId;
        await client.graphql({ query: createProcessedModelSchedule, variables: { input } });
      }
      endOp(opId);
    } catch (e) {
      console.log("reconcileProcessedModelSchedules: upsert failed", e?.message);
      endOp(opId, e?.message || "Failed to save schedule");
    }
  }

  for (const row of existing) {
    if (keptIds.has(row.id)) continue;
    const opId = startOp("Schedule: removing");
    try {
      await client.graphql({
        query: deleteProcessedModelScheduleMutation,
        variables: { input: { id: row.id } },
      });
      endOp(opId);
    } catch (e) {
      console.log("reconcileProcessedModelSchedules: delete failed", e?.message);
      endOp(opId, e?.message || "Failed to remove schedule");
    }
  }
}

// Re-based fire time for a row whose nextRunAt has passed; null when it is still future or has no cron to recompute from (legacy firstRunTime-only rows are left as they are).
function rebasedNextRunAt(row, nowMs) {
  const current = new Date(row.nextRunAt).getTime();
  if (!Number.isNaN(current) && current > nowMs) return null;
  if (!row.cronExpression) return null;
  // Default to UTC exactly as deriveScheduleTiming does, so both paths re-base identically
  return computeNextRunFromCron(row.cronExpression, row.timezone || "UTC") || null;
}

/** Flip a schema's schedule rows ACTIVE/INACTIVE to match its isActive state. The poller queries row status, not ProcessedModelSchema.isActive — without this the pill says "Inactive" while the model keeps running on schedule. Deliberately NOT reconcile(): that deletes any row absent from the config list. Here we only update status in place, so nothing is ever lost. ONCE rows are handled asymmetrically: activating skips them (they have already fired, and re-activating one would re-run the model), but deactivating must include any still-ACTIVE ONCE row — triggerRunOnce creates one, and leaving it ACTIVE runs the model the user just switched off. Activating also re-bases a stale nextRunAt: the poller claims rows on status=ACTIVE AND nextRunAt<=now, so re-enabling a row parked in the past fires an immediate unrequested run. Returns { failed } so the caller can surface a partial failure rather than lying to the user. */
export async function setScheduleRowsStatus(schemaId, nextActive) {
  if (!schemaId) return { failed: 0, updated: 0 };
  const status = nextActive ? "ACTIVE" : "INACTIVE";

  let rows = [];
  try {
    rows = await fetchSchedulesForSchema(schemaId);
  } catch (e) {
    console.warn("setScheduleRowsStatus: could not list schedules", e?.message);
    return { failed: -1, updated: 0 }; // unknown — listing failed, rows may be out of sync
  }

  const targets = rows.filter((r) => {
    if (String(r.frequency || "").toUpperCase() !== "ONCE") return true;
    // Only a pending ONCE row is worth cancelling; a spent one is already INACTIVE
    return !nextActive && String(r.status || "").toUpperCase() === "ACTIVE";
  });
  const nowMs = Date.now();
  let failed = 0;
  let updated = 0;
  for (const row of targets) {
    const input = { id: row.id, status };
    // Only on activate — a deactivated row is never claimed, so its nextRunAt can stay put
    if (nextActive) {
      const rebased = rebasedNextRunAt(row, nowMs);
      if (rebased) input.nextRunAt = rebased;
    }
    try {
      await client.graphql({
        query: updateProcessedModelSchedule,
        variables: { input },
      });
      updated++;
    } catch (e) {
      failed++;
      console.warn(`setScheduleRowsStatus: failed to update schedule ${row.id}`, e?.message);
    }
  }
  return { failed, updated };
}

// How long a queued run can sit before the poller picks it up. Mirrors the CloudWatch rate in amplify/backend/function/processedModelPollerFunction/parameters.json — change both together.
export const POLLER_INTERVAL_LABEL = "about 5 minutes";
export const QUEUED_BUTTON_HOLD_MS = 60 * 1000;

/** Queue an immediate one-off run of a schema. The poller (rate(5 minutes)) claims ONCE rows with a conditional write, flips them INACTIVE and async-invokes the executor — so this fires exactly once and never touches the model's real schedules. It is queued, not immediate: the run starts on the next poll tick. No-ops (returning the existing row id) when a run is already pending for this schema. */
export async function triggerRunOnce(schema) {
  if (!schema?.id) throw new Error("triggerRunOnce: missing schema id");

  // The caller's in-page guard is lost on reload, so re-check the rows themselves — a second pending ONCE row would make the poller run the model twice.
  const pending = (await fetchSchedulesForSchema(schema.id)).find(
    (r) =>
      String(r.frequency || "").toUpperCase() === "ONCE" &&
      String(r.status || "").toUpperCase() === "ACTIVE"
  );
  if (pending) return pending.id;

  const now = new Date().toISOString();
  const input = {
    id: uuid(),
    processedModelSchemaId: schema.id,
    status: "ACTIVE",
    frequency: "ONCE",
    nextRunAt: now,
    firstRunTime: now,
  };
  if (schema.owner) input.owner = schema.owner;
  if (schema.organizationId) input.organizationId = schema.organizationId;

  await client.graphql({ query: createProcessedModelSchedule, variables: { input } });
  return input.id;
}

/** Delete every ProcessedModelSchedule row for a schema (used when the schema is deleted). Best-effort, but returns { failed } so the caller can surface orphan rows — a row that fails to delete keeps the poller firing the executor for a schema that no longer exists. */
export async function removeAllProcessedModelSchedules(schemaId) {
  if (!schemaId) return { failed: 0 };
  let rows = [];
  try {
    rows = await fetchSchedulesForSchema(schemaId);
  } catch (e) {
    console.warn("removeAllProcessedModelSchedules: could not list schedules to clean up", e?.message);
    return { failed: -1 }; // unknown — listing failed, orphans may remain
  }
  let failed = 0;
  for (const row of rows) {
    try {
      await client.graphql({
        query: deleteProcessedModelScheduleMutation,
        variables: { input: { id: row.id } },
      });
    } catch (e) {
      failed++;
      console.warn(`removeAllProcessedModelSchedules: failed to delete schedule ${row.id}`, e?.message);
    }
  }
  return { failed };
}
